import { ChevronDown, ChevronUp, SlidersHorizontal } from "lucide-react";
import { memo, useState } from "react";

import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/button/Button";
import { Checkbox } from "@/shared/ui/checkbox/Checkbox";

import type { FacetGroup, FacetKey } from "../lib/facets";

/** Rows shown per group before "Show N more". */
const COLLAPSED_ROWS = 6;

interface FacetRailProps {
  groups: FacetGroup[];
  activeCount: number;
  onToggle: (key: FacetKey, value: string) => void;
  onClearAll: () => void;
}

/** The filter rail beside the results: one checkbox group per facet, live counts, clear-all. */
export const FacetRail = memo(function FacetRail({
  groups,
  activeCount,
  onToggle,
  onClearAll,
}: FacetRailProps) {
  return (
    <aside
      aria-label="Filters"
      className="flex flex-col gap-4 border-b border-border bg-muted/40 px-4 py-3 md:border-b-0 md:border-r"
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
          Filters
          {activeCount > 0 && (
            <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
              {activeCount}
            </span>
          )}
        </span>
        {activeCount > 0 && (
          <Button
            type="button"
            variant="ghost"
            onClick={onClearAll}
            className="h-auto px-1.5 py-0.5 text-[11px] text-primary hover:bg-transparent hover:underline"
          >
            Clear all
          </Button>
        )}
      </div>
      {groups.map((g) => (
        <FacetGroupList key={g.key} group={g} onToggle={onToggle} />
      ))}
    </aside>
  );
});

const FacetGroupList = memo(function FacetGroupList({
  group,
  onToggle,
}: {
  group: FacetGroup;
  onToggle: (key: FacetKey, value: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const overflow = group.options.length - COLLAPSED_ROWS;
  const visible =
    expanded || overflow <= 0 ? group.options : group.options.slice(0, COLLAPSED_ROWS);
  return (
    <fieldset className="min-w-0">
      <legend className="mb-1.5 text-xs font-semibold text-foreground">{group.title}</legend>
      <ul className="space-y-0.5">
        {visible.map((o) => {
          const id = `facet-${group.key}-${o.value.replace(/\W+/g, "-")}`;
          return (
            <li key={o.value}>
              <label
                htmlFor={id}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-xs hover:bg-muted",
                  o.count === 0 && !o.selected && "text-muted-foreground"
                )}
              >
                <Checkbox
                  id={id}
                  checked={o.selected}
                  onCheckedChange={() => onToggle(group.key, o.value)}
                  aria-label={`${o.label} (${o.count})`}
                />
                <span className="min-w-0 flex-1 truncate" title={o.label}>
                  {o.label}
                </span>
                <span className="tabular-nums text-[11px] text-muted-foreground">{o.count}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {overflow > 0 && (
        <Button
          type="button"
          variant="ghost"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 h-auto px-1 py-0.5 text-[11px] text-primary hover:bg-transparent hover:underline"
        >
          {expanded ? (
            <>
              <ChevronUp className="mr-1 h-3 w-3" aria-hidden="true" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="mr-1 h-3 w-3" aria-hidden="true" />
              Show {overflow} more
            </>
          )}
        </Button>
      )}
    </fieldset>
  );
});
